import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { searchCustomersForFilter } from '../../utils/customerSearchMerge';
import { parseCustomerListSearchQuery } from '../../utils/customerSearchFilter';

const inputStyle = {
    padding: '8px 12px',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    width: '100%',
    outline: 'none',
    boxSizing: 'border-box',
    height: '38px',
    fontSize: '14px',
};

const dropdownItemStyle = {
    display: 'block',
    width: '100%',
    padding: '10px 12px',
    border: 'none',
    background: 'transparent',
    textAlign: 'left',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#111827',
};

const badgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 10px',
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: '4px',
    fontSize: '13px',
    color: '#1e40af',
    maxWidth: '100%',
};

const clearButtonStyle = {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    padding: '0 2px',
    fontSize: '14px',
    lineHeight: 1,
    color: '#1e40af',
};

/**
 * Filtro/busca de cliente via `/api/pessoa-juridica` (mesma consulta do CustomerSelector).
 *
 * @param {{
 *   label?: string,
 *   inputId?: string,
 *   selectedCustomerId?: string|number|null,
 *   selectedCustomerName?: string,
 *   onSelect: (customer: { id: number|string, name: string, type?: string }|null) => void,
 *   disabled?: boolean,
 *   dropdownZIndex?: number,
 *   includePhysicalCustomers?: boolean,
 * }} props
 */
export default function RouteCustomerFilter({
    label = 'Cliente',
    inputId,
    selectedCustomerId,
    selectedCustomerName,
    onSelect,
    disabled = false,
    dropdownZIndex = 10050,
    includePhysicalCustomers = false,
}) {
    const generatedId = useId();
    const fieldId = inputId || generatedId;
    const [localSearchQuery, setLocalSearchQuery] = useState('');
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [dropdownRect, setDropdownRect] = useState(null);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);

    const searchQuery = useDebouncedValue(localSearchQuery, 750);
    const hasSelection = selectedCustomerId != null && String(selectedCustomerId).trim() !== '';

    const fetchCustomers = useCallback(
        async (query) => {
            const { name, document } = parseCustomerListSearchQuery(query);
            if (!name && !document) {
                setCustomers([]);
                return;
            }

            setLoading(true);
            try {
                const customerList = await searchCustomersForFilter(query, {
                    includePhysical: includePhysicalCustomers,
                    size: 8,
                });
                setCustomers(customerList);
            } catch {
                setCustomers([]);
            } finally {
                setLoading(false);
            }
        },
        [includePhysicalCustomers],
    );

    useEffect(() => {
        if (hasSelection || disabled) {
            setCustomers([]);
            return;
        }
        const { name, document } = parseCustomerListSearchQuery(searchQuery);
        if (!name && !document) {
            setCustomers([]);
            setDropdownOpen(false);
            return;
        }
        fetchCustomers(searchQuery);
        setDropdownOpen(true);
    }, [searchQuery, hasSelection, disabled, fetchCustomers]);

    useEffect(() => {
        if (!dropdownOpen) {
            setDropdownRect(null);
            return undefined;
        }

        const updatePosition = () => {
            const el = inputRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            setDropdownRect({
                top: rect.bottom + 4,
                left: rect.left,
                width: rect.width,
            });
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [dropdownOpen, customers, loading, localSearchQuery]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const inContainer = containerRef.current?.contains(event.target);
            const inDropdown = dropdownRef.current?.contains(event.target);
            if (!inContainer && !inDropdown) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelectCustomer = (customer) => {
        onSelect({
            id: customer.id,
            name: customer.name ?? `Cliente #${customer.id}`,
            type: customer.type,
        });
        setLocalSearchQuery('');
        setCustomers([]);
        setDropdownOpen(false);
    };

    const handleClear = () => {
        onSelect(null);
        setLocalSearchQuery('');
        setCustomers([]);
        setDropdownOpen(false);
    };

    if (hasSelection) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                <span style={{ fontSize: '12px', fontWeight: 500, color: '#333' }}>{label}</span>
                <div style={badgeStyle} title={selectedCustomerName || undefined}>
                    <span
                        style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {selectedCustomerName || `Cliente #${selectedCustomerId}`}
                    </span>
                    <button
                        type="button"
                        onClick={handleClear}
                        disabled={disabled}
                        style={clearButtonStyle}
                        aria-label={`Remover filtro de ${label.toLowerCase()}`}
                        title={`Remover filtro de ${label.toLowerCase()}`}
                    >
                        ✕
                    </button>
                </div>
            </div>
        );
    }

    const showDropdown =
        dropdownOpen && dropdownRect && (loading || customers.length > 0 || searchQuery.trim());

    return (
        <div
            ref={containerRef}
            style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0, position: 'relative' }}
        >
            <label htmlFor={fieldId} style={{ fontSize: '12px', fontWeight: 500, color: '#333' }}>
                {label}
            </label>
            <input
                ref={inputRef}
                id={fieldId}
                type="text"
                placeholder="Nome ou CPF / CNPJ"
                value={localSearchQuery}
                onChange={(e) => setLocalSearchQuery(e.target.value)}
                onFocus={() => {
                    if (customers.length > 0 || localSearchQuery.trim()) {
                        setDropdownOpen(true);
                    }
                }}
                disabled={disabled}
                style={inputStyle}
                autoComplete="off"
            />
            {showDropdown
                ? createPortal(
                    <div
                        ref={dropdownRef}
                        role="listbox"
                        aria-label="Resultados de clientes"
                        style={{
                            position: 'fixed',
                            top: dropdownRect.top,
                            left: dropdownRect.left,
                            width: dropdownRect.width,
                            background: '#fff',
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                            maxHeight: '240px',
                            overflowY: 'auto',
                            zIndex: dropdownZIndex,
                        }}
                    >
                        {loading ? (
                            <div style={{ padding: '10px 12px', fontSize: '13px', color: '#6b7280' }}>
                                Buscando clientes…
                            </div>
                        ) : customers.length === 0 ? (
                            <div style={{ padding: '10px 12px', fontSize: '13px', color: '#6b7280' }}>
                                Nenhum cliente encontrado.
                            </div>
                        ) : (
                            customers.map((customer) => (
                                <button
                                    key={`${customer.type || 'PJ'}-${customer.id}`}
                                    type="button"
                                    role="option"
                                    aria-selected={false}
                                    style={dropdownItemStyle}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = '#f3f4f6';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'transparent';
                                    }}
                                    onClick={() => handleSelectCustomer(customer)}
                                >
                                    <span style={{ fontWeight: 500 }}>{customer.name || '—'}</span>
                                    {customer.subtitle || customer.document ? (
                                        <span
                                            style={{
                                                display: 'block',
                                                fontSize: '11px',
                                                color: '#6b7280',
                                                marginTop: 2,
                                            }}
                                        >
                                            {customer.subtitle || customer.document}
                                        </span>
                                    ) : null}
                                </button>
                            ))
                        )}
                    </div>,
                    document.body
                )
                : null}
        </div>
    );
}
